import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import {
  CheckCircle2,
  XCircle,
  Lock,
  Eraser,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Logo from "@/components/Logo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PdfCanvas from "@/components/PdfCanvas";

function todayStr() {
  const d = new Date();
  return d.toLocaleDateString();
}

function SignaturePad({ onSave, onClose, label = "Draw your signature" }) {
  const ref = useRef(null);
  const [typed, setTyped] = useState("");
  const [mode, setMode] = useState("draw");

  const clear = () => ref.current?.clear();

  const save = () => {
    if (mode === "draw") {
      if (!ref.current || ref.current.isEmpty()) {
        return toast.error("Please draw your signature.");
      }
      onSave(ref.current.getCanvas().toDataURL("image/png"));
    } else {
      if (!typed.trim()) return toast.error("Type your name.");
      // Build an SVG data URL
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='100'><text x='10' y='70' font-family='Caveat, cursive' font-size='56' fill='#0A1128'>${typed.replace(
        /</g,
        "&lt;",
      )}</text></svg>`;
      onSave(`data:image/svg+xml;base64,${btoa(svg)}`);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Button
          size="sm"
          variant={mode === "draw" ? "default" : "outline"}
          onClick={() => setMode("draw")}
          className={mode === "draw" ? "bg-slate-900 text-white" : ""}
          data-testid="sig-mode-draw"
        >
          Draw
        </Button>
        <Button
          size="sm"
          variant={mode === "type" ? "default" : "outline"}
          onClick={() => setMode("type")}
          className={mode === "type" ? "bg-slate-900 text-white" : ""}
          data-testid="sig-mode-type"
        >
          Type
        </Button>
      </div>

      {mode === "draw" ? (
        <div>
          <SignatureCanvas
            ref={ref}
            penColor="#0A1128"
            canvasProps={{
              className: "sig-canvas w-full",
              width: 520,
              height: 160,
              "data-testid": "signature-canvas",
            }}
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-xs text-slate-500">{label}</div>
            <Button variant="ghost" size="sm" onClick={clear}>
              <Eraser className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Input
            placeholder="Type your full name"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="h-11"
            data-testid="sig-type-input"
          />
          <div
            className="mt-3 h-24 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-900"
            style={{ fontFamily: "Caveat, cursive", fontSize: 40 }}
          >
            {typed || "Your signature"}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mt-5">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={save}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="sig-apply"
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

export default function Signer() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [verifyState, setVerifyState] = useState(null); // { method, recipient, title, sender_name } or null
  const [verifyValue, setVerifyValue] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [values, setValues] = useState({}); // field_id -> value
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sigField, setSigField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/sign/${token}`);
      if (data?.verification_required) {
        setVerifyState(data);
        setLoading(false);
        return;
      }
      setVerifyState(null);
      setDoc(data);
      setNumPages(data.page_count || 1);
      // Prefill full_name/date fields automatically; also carry any sender-prefilled
      // value (e.g., rich-text offer-letter body) into the values dict so it counts
      // toward completedCount and gets submitted back.
      const prefill = {};
      for (const f of data.fields || []) {
        if (f.recipient_id !== data.recipient.id) continue;
        if (f.value) {
          prefill[f.id] = f.value;
          continue;
        }
        if (f.type === "full_name") prefill[f.id] = data.recipient.name;
        if (f.type === "email") prefill[f.id] = data.recipient.email;
        if (f.type === "date") prefill[f.id] = todayStr();
      }
      setValues(prefill);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (!verifyValue.trim()) return toast.error("Enter the required value.");
    setVerifying(true);
    try {
      await api.post(`/sign/${token}/verify`, {
        kind: verifyState.method,
        value: verifyValue.trim(),
      });
      setVerifyValue("");
      setLoading(true);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [token]);

  const setVal = (fid, v) => setValues({ ...values, [fid]: v });

  const submit = async () => {
    if (!doc) return;
    const myFields = doc.fields.filter((f) => f.recipient_id === doc.recipient.id);
    for (const f of myFields) {
      if (f.required && !values[f.id]) {
        return toast.error(`Please complete the "${f.type}" field on page ${f.page}.`);
      }
    }
    setSubmitting(true);
    try {
      await api.post(`/sign/${token}/submit`, {
        fields: Object.entries(values).map(([field_id, value]) => ({ field_id, value })),
      });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const decline = async () => {
    setSubmitting(true);
    try {
      await api.post(`/sign/${token}/decline`, { reason: declineReason });
      setDeclineOpen(false);
      setDone("declined");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Loading document…
      </div>
    );
  }

  if (verifyState) {
    const isOtp = verifyState.method === "otp";
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-slate-50"
        data-testid="signer-verify-page"
      >
        <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 p-8">
          <div className="flex items-center justify-center mb-4">
            <Logo className="h-8" />
          </div>
          <div className="text-center">
            <Lock className="h-8 w-8 text-emerald-600 mx-auto" />
            <h1 className="mt-3 text-xl font-bold tracking-tight">Verify it's you</h1>
            <p className="mt-2 text-sm text-slate-500">
              {isOtp
                ? "Enter the 6-digit code your sender shared with you to access this document."
                : "Confirm your email address to access this document."}
            </p>
            <div className="mt-3 text-xs font-mono text-slate-500">
              {verifyState.title} · {verifyState.recipient.email_hint}
            </div>
          </div>
          <div className="mt-6">
            <Label className="text-xs">{isOtp ? "Verification code" : "Email address"}</Label>
            <Input
              autoFocus
              type={isOtp ? "text" : "email"}
              value={verifyValue}
              onChange={(e) => setVerifyValue(e.target.value)}
              placeholder={isOtp ? "123456" : "you@example.com"}
              className="mt-1.5 h-11 text-center font-mono tracking-widest"
              maxLength={isOtp ? 6 : 80}
              onKeyDown={(e) => e.key === "Enter" && verify()}
              data-testid="verify-input"
            />
            <Button
              onClick={verify}
              disabled={verifying || !verifyValue.trim()}
              className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="verify-submit"
            >
              {verifying ? "Verifying…" : "Continue"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // Wrong-environment safety net: when a signing link was generated on a different
    // env (production email pointing to preview, or vice versa) and the token isn't
    // found here, offer the user a one-click redirect to the alternative environment.
    const here = (typeof window !== "undefined" ? window.location.host : "").toLowerCase();
    const isPreviewHere = here.includes("preview.emergentagent.com");
    const isProdHere = here.includes("attestrasign.com");
    let altUrl = "";
    if (isPreviewHere) altUrl = `https://attestrasign.com${window.location.pathname}`;
    else if (isProdHere) {
      // Best-effort guess at the matching preview slug
      altUrl = `https://attestra-sign.preview.emergentagent.com${window.location.pathname}`;
    }
    const looksMisrouted = /not\s*found|invalid|expired/i.test(error);
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center bg-white rounded-xl border border-slate-200 p-8">
          <XCircle className="h-10 w-10 text-red-500 mx-auto" />
          <h1 className="mt-4 text-xl font-semibold">Unable to open document</h1>
          <p className="mt-2 text-sm text-slate-500">{error}</p>
          {altUrl && looksMisrouted && (
            <div className="mt-6 border-t border-slate-100 pt-5 text-sm text-slate-600">
              <p>
                This link may belong to a different Attestra environment. Try opening
                it on:
              </p>
              <a
                href={altUrl}
                className="mt-3 inline-flex items-center justify-center rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-medium"
                data-testid="signer-redirect-alt"
              >
                Try {isPreviewHere ? "attestrasign.com" : "the preview environment"}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (done) {
    const isDeclined = done === "declined";
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-md w-full text-center bg-white rounded-xl border border-slate-200 p-10">
          {isDeclined ? (
            <XCircle className="h-12 w-12 text-red-500 mx-auto" />
          ) : (
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
          )}
          <h1 className="mt-4 text-2xl font-bold tracking-tight">
            {isDeclined ? "Document declined" : "All signed"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {isDeclined
              ? "Thanks — we've recorded your decline."
              : "Thanks for completing this document. A copy will be kept in Attestra."}
          </p>
          <div className="mt-6 text-xs text-slate-500 font-mono">
            Document ID · {doc?.id?.slice(0, 8)}
          </div>
        </div>
      </div>
    );
  }

  const myFields = doc.fields.filter((f) => f.recipient_id === doc.recipient.id);
  const otherFields = doc.fields.filter((f) => f.recipient_id !== doc.recipient.id);

  const renderOverlayField = (f) => {
    const mine = f.recipient_id === doc.recipient.id;
    const filled = values[f.id];
    const boxStyle = {
      left: f.x,
      top: f.y,
      width: f.width,
      height: f.height,
    };

    if (!mine) {
      return (
        <div
          key={f.id}
          className="absolute rounded-sm border border-dashed border-slate-300 bg-slate-100/60"
          style={boxStyle}
        />
      );
    }

    const base =
      "absolute rounded-sm border-2 bg-emerald-50/40 cursor-pointer flex items-center justify-center";
    const color = filled ? "border-emerald-500" : "border-emerald-400 animate-pulse";

    if (f.type === "signature" || f.type === "initials") {
      return (
        <div
          key={f.id}
          className={`${base} ${color}`}
          style={boxStyle}
          onClick={() => setSigField(f)}
          data-testid={`sign-field-${f.id}`}
        >
          {filled ? (
            <img src={filled} alt="" className="max-h-full max-w-full" />
          ) : (
            <span className="text-xs text-emerald-700 font-medium">
              {f.type === "signature" ? "Click to sign" : "Initials"}
            </span>
          )}
        </div>
      );
    }

    if (f.type === "checkbox") {
      return (
        <div
          key={f.id}
          className={`${base} ${color}`}
          style={boxStyle}
          onClick={() => setVal(f.id, filled ? "" : "checked")}
          data-testid={`sign-field-${f.id}`}
        >
          {filled && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
        </div>
      );
    }

    // Rich-text / multiline text fields: if sender has pre-filled content (typically
    // HTML from TipTap), render it READ-ONLY as rich text. Otherwise show an editable
    // textarea so the signer can fill in a longer response.
    if (f.type === "text") {
      const hasContent = !!(values[f.id] || f.value);
      if (hasContent) {
        // Strip script tags as a minimal XSS guard. The HTML originates from our
        // own TipTap editor controlled by the org-admin sender.
        const safe = String(values[f.id] || f.value || "").replace(
          /<\s*script[^>]*>[\s\S]*?<\s*\/\s*script\s*>/gi,
          "",
        );
        return (
          <div
            key={f.id}
            className="absolute rounded-sm border-2 border-emerald-400 bg-white overflow-hidden rt-readonly"
            style={{ ...boxStyle, padding: "4px 6px" }}
            data-testid={`sign-field-${f.id}`}
            dangerouslySetInnerHTML={{ __html: safe }}
          />
        );
      }
      return (
        <textarea
          key={f.id}
          value={values[f.id] || ""}
          placeholder="Text"
          onChange={(e) => setVal(f.id, e.target.value)}
          className="absolute rounded-sm border-2 border-emerald-400 bg-emerald-50/40 px-1 py-1 text-xs text-slate-900 focus:outline-none focus:border-emerald-600 resize-none"
          style={boxStyle}
          data-testid={`sign-field-${f.id}`}
        />
      );
    }

    return (
      <input
        key={f.id}
        type={f.type === "email" ? "email" : f.type === "phone" ? "tel" : "text"}
        value={values[f.id] || ""}
        placeholder={f.type}
        onChange={(e) => setVal(f.id, e.target.value)}
        className="absolute rounded-sm border-2 border-emerald-400 bg-emerald-50/40 px-1 text-xs text-slate-900 focus:outline-none focus:border-emerald-600"
        style={boxStyle}
        data-testid={`sign-field-${f.id}`}
      />
    );
  };

  const visibleFields = [...myFields, ...otherFields].filter((f) => f.page === page);
  const completedCount = myFields.filter((f) => values[f.id]).length;
  const progress = myFields.length ? (completedCount / myFields.length) * 100 : 0;

  const accent = doc.org?.branding?.accent_color || "#059669";
  const orgName = doc.org?.name;
  const branding = doc.org?.branding || {};
  const orgLogo = branding.signer_logo_url || branding.logo_data_url;
  const heading = branding.signer_heading;
  const body = branding.signer_body;
  const footer = branding.signer_footer;
  const ctaLabel = branding.signer_cta_label || "Finish & sign";
  const accentBtnStyle = { backgroundColor: accent, borderColor: accent };

  return (
    <div
      className="min-h-[100dvh] bg-slate-50 pb-[max(env(safe-area-inset-bottom),8rem)]"
      data-testid="signer-page"
    >
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            {orgLogo ? (
              <img src={orgLogo} alt={orgName || "Logo"} className="h-7 max-w-[180px] object-contain" />
            ) : (
              <Logo className="h-7" />
            )}
            {orgName && (
              <span
                className="text-sm font-semibold tracking-tight"
                style={{ color: accent }}
              >
                {orgName}
              </span>
            )}
          </Link>
          <div className="hidden sm:block text-xs text-slate-500 font-mono truncate">
            {doc.title} · from <span className="text-slate-700">{doc.sender_name}</span>
          </div>
        </div>
        <div className="h-1 bg-slate-100">
          <div
            className="h-full transition-all"
            style={{ width: `${progress}%`, backgroundColor: accent }}
          />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {(heading || body) && (
          <div
            className="rounded-xl border bg-white p-5 mb-5"
            style={{ borderColor: `${accent}33` }}
            data-testid="signer-custom-greeting"
          >
            {heading && (
              <div
                className="text-xl font-semibold"
                style={{ color: accent }}
                data-testid="signer-custom-heading"
              >
                {heading}
              </div>
            )}
            {body && (
              <div
                className="mt-2 text-sm text-slate-600 whitespace-pre-wrap"
                data-testid="signer-custom-body"
              >
                {body}
              </div>
            )}
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">You're signing as</div>
          <div className="mt-1 text-lg font-semibold">{doc.recipient.name}</div>
          <div className="text-xs text-slate-500">{doc.recipient.email}</div>
          {doc.message && (
            <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
              {doc.message}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(Math.max(1, page - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-xs font-mono text-slate-600">
            Page {page} / {numPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= numPages}
            onClick={() => setPage(Math.min(numPages, page + 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-5">
          <PdfCanvas
            fileUrl={doc.file_data_url}
            pageNumber={page}
            onLoadPages={(n) => setNumPages(n)}
            renderOverlay={() => <>{visibleFields.map(renderOverlayField)}</>}
          />
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-30 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-xs text-slate-500">Progress</div>
            <div className="text-sm font-semibold">
              {completedCount}/{myFields.length} fields completed
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setDeclineOpen(true)}
              data-testid="signer-decline-btn"
            >
              Decline
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || completedCount < myFields.length}
              className="text-white hover:opacity-90"
              style={accentBtnStyle}
              data-testid="signer-submit-btn"
            >
              {submitting ? "Submitting…" : ctaLabel}
            </Button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-3 -mt-1 flex items-center gap-2 text-[11px] text-slate-500">
          <Lock className="h-3 w-3" /> Secured by Attestra · your actions are audit-logged.
        </div>
        {footer && (
          <div
            className="max-w-5xl mx-auto px-4 sm:px-6 pb-3 text-[11px] text-slate-500 whitespace-pre-wrap"
            data-testid="signer-custom-footer"
          >
            {footer}
          </div>
        )}
      </div>

      {/* Signature dialog */}
      <Dialog open={!!sigField} onOpenChange={(o) => !o && setSigField(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {sigField?.type === "initials" ? "Add your initials" : "Add your signature"}
            </DialogTitle>
          </DialogHeader>
          {sigField && (
            <SignaturePad
              label="By signing, you agree this is a legally binding digital signature."
              onClose={() => setSigField(null)}
              onSave={(dataUrl) => {
                setVal(sigField.id, dataUrl);
                setSigField(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline to sign</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Let the sender know why you're declining. This action is final.
          </p>
          <Label className="text-sm font-medium mt-3 block">Reason (optional)</Label>
          <Textarea
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="I need to change the terms on page 2…"
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={decline}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="signer-decline-confirm"
            >
              Confirm decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
